/**
 * What a browser's user agent says about the device, for the list of devices
 * signed in (Configuració): the owner could not tell two "Chrome · Windows"
 * rows apart (test card 151, 2026-10-17), so a row names the browser and the
 * system with their versions, the kind of device and, where the agent carries
 * one, its model. A browser may hide most of this (Chrome's reduced agent sends
 * "Android 10; K" from every phone), so every part is optional and a part the
 * agent does not carry is left out, never guessed.
 */
export type DeviceKind = 'phone' | 'tablet' | 'computer';

export interface DeviceDescription {
  kind: DeviceKind;
  /** The browser, with its major version when the agent gives it: "Chrome 120". */
  browser: string;
  /** The system, with its version when it is worth telling: "Android 14", "iPhone (iOS 17.4)". */
  os: string;
  /** The phone's or tablet's model when the agent names one ("Pixel 8"); empty otherwise. */
  model: string;
}

const major = (ua: string, re: RegExp) => ua.match(re)?.[1] ?? '';

function browserOf(ua: string): string {
  const named: [string, RegExp][] = [
    ['Edge', /Edg(?:e|A|iOS)?\/(\d+)/],
    ['Opera', /(?:OPR|OPT)\/(\d+)/],
    ['Samsung Internet', /SamsungBrowser\/(\d+)/],
    ['Firefox', /(?:Firefox|FxiOS)\/(\d+)/],
    ['Chrome', /(?:Chrome|CriOS)\/(\d+)/],
  ];
  for (const [name, re] of named) {
    if (re.test(ua)) return `${name} ${major(ua, re)}`.trim();
  }
  if (/Safari\//.test(ua)) return `Safari ${major(ua, /Version\/(\d+(?:\.\d+)?)/)}`.trim();
  return ua.split(/[\s/]/)[0];
}

const dotted = (v: string) => v.replace(/_/g, '.');

function osOf(ua: string): string {
  const ios = dotted(major(ua, /(?:iPhone )?OS (\d+[_.]\d+)/));
  if (/iPad/.test(ua)) return ios ? `iPad (iPadOS ${ios})` : 'iPad';
  if (/iPhone|iPod/.test(ua)) return ios ? `iPhone (iOS ${ios})` : 'iPhone';
  if (/Android/.test(ua)) return `Android ${major(ua, /Android (\d+(?:\.\d+)?)/)}`.trim();
  if (/CrOS/.test(ua)) return 'ChromeOS';
  const nt = major(ua, /Windows NT (\d+\.\d+)/);
  if (/Windows/.test(ua)) {
    // NT 10.0 is Windows 10 and 11 alike: the agent does not tell them apart.
    const named: Record<string, string> = {
      '6.1': 'Windows 7',
      '6.2': 'Windows 8',
      '6.3': 'Windows 8.1',
    };
    return named[nt] ?? 'Windows';
  }
  if (/Mac OS|Macintosh/.test(ua)) return 'Mac';
  if (/Linux/.test(ua)) return 'Linux';
  return '';
}

/** The model between "Android 14;" and ")" or " Build", unless the browser hid it ("K"). */
function modelOf(ua: string): string {
  const m = ua.match(/Android [\d.]+;\s*([^;)]+?)(?:\s+Build\/[^;)]*)?\s*[;)]/);
  const model = m?.[1]?.trim() ?? '';
  return model.length > 1 && !/^(wv|K|Mobile|[a-z]{2}[-_][A-Za-z]{2})$/.test(model) ? model : '';
}

function kindOf(ua: string): DeviceKind {
  if (/iPad|Tablet/.test(ua)) return 'tablet';
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? 'phone' : 'tablet';
  if (/iPhone|iPod|Mobile/.test(ua)) return 'phone';
  return 'computer';
}

export function describeAgent(ua: string): DeviceDescription {
  return { kind: kindOf(ua), browser: browserOf(ua), os: osOf(ua), model: modelOf(ua) };
}

/** The row's name: "Chrome 120 · Windows", "Chrome 131 · Android 14 · Pixel 8". */
export function deviceTitle(d: DeviceDescription): string {
  return [d.browser, d.os, d.model].filter(Boolean).join(' · ');
}

/** The Bootstrap icon of a kind of device. */
export const DEVICE_ICONS: Record<DeviceKind, string> = {
  phone: 'phone',
  tablet: 'tablet',
  computer: 'laptop',
};
